import React from 'react';

/**
 * HeaderBlock renders applicant name and clean layout contact links/details.
 * Emphasizes visual identity: Classic, Centered, Split, Executive, Creative, Hero.
 */
export const HeaderBlock = ({ personalInfo = {}, layout = 'classic' }) => {
  const info = personalInfo || {};
  const name = info.name || 'Your Name';
  const roleTitle = info.roleTitle || info.role || '';
  const email = info.email || '';
  const phone = info.phone || '';
  const location = info.location || info.city || '';
  
  const links = info.links || {};
  const linkedin = links.linkedin || info.linkedin || '';
  const github = links.github || info.github || '';
  const portfolio = links.portfolio || info.portfolio || info.website || '';

  const stripUrl = (url) => {
    if (!url) return '';
    return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  };

  const formatLinkedin = (urlOrUser) => {
    if (!urlOrUser) return '';
    const clean = stripUrl(urlOrUser);
    if (clean.includes('linkedin.com')) return clean;
    return `linkedin.com/in/${clean}`;
  };

  const formatGithub = (urlOrUser) => {
    if (!urlOrUser) return '';
    const clean = stripUrl(urlOrUser);
    if (clean.includes('github.com')) return clean;
    return `github.com/${clean}`;
  };

  const contacts = [
    location ? `📍 ${location}` : null,
    email ? `✉ ${email}` : null,
    phone ? `☎ ${phone}` : null,
    linkedin ? `🔗 ${formatLinkedin(linkedin)}` : null,
    github ? `💻 ${formatGithub(github)}` : null,
    portfolio ? `🔗 ${stripUrl(portfolio)}` : null,
  ].filter(Boolean);

  const isCentered = layout === 'centered';

  // 1. Split Layout
  if (layout === 'split') {
    return (
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: 'var(--spacing-section-mb, 16px)',
          borderBottom: 'var(--border-thickness-heading, 1.5px) solid var(--color-border, #E2E8F0)',
          paddingBottom: '16px',
          width: '100%'
        }}
      >
        <div>
          <h1 
            style={{ 
              fontSize: 'var(--font-size-display, 26pt)', 
              fontWeight: 'var(--weight-display, 800)', 
              color: 'var(--color-primary, #16324F)', 
              margin: '0 0 6px 0', 
              textTransform: 'uppercase',
              letterSpacing: 'var(--letter-spacing-display, 0.5px)' 
            }}
          >
            {name}
          </h1>
          {roleTitle && (
            <div style={{ fontSize: 'var(--font-size-subheading, 9.5pt)', fontWeight: 'var(--weight-subheading, 700)', color: 'var(--color-secondary, #204F73)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {roleTitle}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #777777)', gap: '4px' }}>
          {contacts.map((c, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>{c}</span>)}
        </div>
      </div>
    );
  }

  // 2. Executive Layout
  if (layout === 'executive') {
    return (
      <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-section-mb, 16px)', width: '100%' }}>
        <h1 
          style={{ 
            fontSize: 'var(--font-size-display, 26pt)', 
            fontWeight: 'var(--weight-display, 800)', 
            color: 'var(--color-primary, #16324F)', 
            margin: '0 0 8px 0', 
            textTransform: 'uppercase', 
            fontFamily: 'var(--font-family-primary, Georgia, serif)',
            letterSpacing: '0.08em'
          }}
        >
          {name}
        </h1>
        {roleTitle && (
          <div 
            style={{ 
              borderTop: '1px solid var(--color-border, #E2E8F0)', 
              borderBottom: '1px solid var(--color-border, #E2E8F0)', 
              padding: '6px 0', 
              margin: '8px auto', 
              maxWidth: '320px', 
              fontSize: 'var(--font-size-subheading, 9.5pt)', 
              fontWeight: 'var(--weight-subheading, 700)', 
              color: 'var(--color-secondary, #204F73)', 
              textTransform: 'uppercase', 
              letterSpacing: '0.15em' 
            }}
          >
            {roleTitle}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px 16px', fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #777777)', marginTop: '8px' }}>
          {contacts.map((c, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>{c}</span>)}
        </div>
      </div>
    );
  }

  // 3. Creative Layout
  if (layout === 'creative') {
    return (
      <div 
        style={{ 
          display: 'flex', 
          gap: '20px', 
          alignItems: 'center', 
          borderLeft: '5px solid var(--color-primary, #16324F)', 
          paddingLeft: '16px', 
          marginBottom: 'var(--spacing-section-mb, 16px)',
          width: '100%'
        }}
      >
        <div>
          <h1 
            style={{ 
              fontSize: 'var(--font-size-display, 26pt)', 
              fontWeight: 'var(--weight-display, 800)', 
              color: 'var(--color-primary, #16324F)', 
              margin: '0 0 6px 0',
              lineHeight: '1.2' 
            }}
          >
            {name}
          </h1>
          {roleTitle && (
            <div style={{ fontSize: 'var(--font-size-subheading, 9.5pt)', fontWeight: 'var(--weight-subheading, 700)', color: 'var(--color-secondary, #204F73)', margin: '0 0 8px 0', letterSpacing: '0.04em' }}>
              {roleTitle}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #777777)' }}>
            {contacts.map((c, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>{c}</span>)}
          </div>
        </div>
      </div>
    );
  }

  // 4. Hero Layout
  if (layout === 'hero') {
    return (
      <div style={{ marginBottom: 'var(--spacing-section-mb, 20px)', width: '100%' }}>
        <h1 
          style={{ 
            fontSize: 'var(--font-size-display, 30pt)', 
            fontWeight: 'var(--weight-display, 900)', 
            color: 'var(--color-primary, #16324F)', 
            margin: '0 0 4px 0', 
            textTransform: 'uppercase', 
            letterSpacing: '-0.5px' 
          }}
        >
          {name}
        </h1>
        {roleTitle && (
          <div style={{ fontSize: 'var(--font-size-subheading, 10pt)', fontWeight: 'var(--weight-subheading, 700)', color: 'var(--color-secondary, #204F73)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>
            {roleTitle}
          </div>
        )}
        <div style={{ height: '4px', backgroundColor: 'var(--color-primary, #16324F)', marginBottom: '12px', width: '60px' }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #777777)' }}>
          {contacts.map((c, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>{c}</span>)}
        </div>
      </div>
    );
  }

  // 5. Classic / Centered Layouts
  return (
    <div style={{ textAlign: isCentered ? 'center' : 'left', marginBottom: 'var(--spacing-section-mb, 16px)', width: '100%' }}>
      {/* Hero Name Block */}
      <h1 
        style={{ 
          fontSize: 'var(--font-size-display, 26pt)', 
          fontWeight: 'var(--weight-display, 800)',
          color: 'var(--color-primary, #16324F)', 
          margin: '0 0 6px 0',
          textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-display, 0.5px)',
          lineHeight: 'var(--line-height-display, 1.2)'
        }}
      >
        {name}
      </h1>
      
      {/* Subtitle / Role Description */}
      {roleTitle && (
        <div 
          style={{ 
            fontSize: 'var(--font-size-subheading, 9.5pt)', 
            fontWeight: 'var(--weight-subheading, 700)', 
            color: 'var(--color-secondary, #204F73)', 
            margin: '0 0 8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}
        >
          {roleTitle}
        </div>
      )}

      {/* Contact row with uniform spacing */}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: isCentered ? 'center' : 'flex-start',
          flexWrap: 'wrap', 
          gap: '8px 16px',
          fontSize: 'var(--font-size-meta, 8pt)', 
          color: 'var(--color-text-light, #777777)',
          lineHeight: 'var(--line-height-meta, 1.2)',
          marginBottom: '8px'
        }}
      >
        {contacts.map((contact, idx) => (
          <span key={idx} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {contact}
            {idx < contacts.length - 1 && (
              <span style={{ marginLeft: '16px', color: 'var(--color-border)', opacity: 0.8 }}>•</span>
            )}
          </span>
        ))}
      </div>
      
      {/* Premium Hero Transition Divider */}
      <hr 
        style={{
          border: 'none',
          borderTop: 'var(--border-thickness-heading, 1.5px) solid var(--color-border, #E2E8F0)',
          marginTop: '12px',
          marginBottom: '16px',
          width: '100%'
        }}
      />
    </div>
  );
};
export default HeaderBlock;
